import { Injectable, OnDestroy } from "@angular/core";
import { Router } from "@angular/router";
import { environment } from "src/environments/environment";
@Injectable({
  providedIn: "root",
})
export class SessionService implements OnDestroy {
  private sessionTimeout = 600000; // 10 minutes in milliseconds (10 * 60 * 1000)
  private logoutTimeout = 1000; // 1 second in milliseconds
  //private sessionTimeout = 7 * 24 * 60 * 60 * 1000; // 7 days * 24 hours/day * 60 minutes/hour * 60 seconds/minute * 1000 milliseconds/second
  private timeoutHandle: number;
  private checkIntervalHandle: number;
  private lastActivityTime: number;
  private readonly LAST_ACTIVITY_KEY = "lastActivityTime";
  private readonly LOGIN_TIMESTAMP_KEY = "loginTimestamp";
  private readonly LOGIN_GRACE_PERIOD = 5000; // 5 seconds grace period after login
  private storage = environment.storage;
  private nodeURL = environment.nodeUrl1;
  public selectedSite = null;
  private visibilityChangeListener: () => void;
  private beforeUnloadListener: () => void;

  constructor(private router: Router) {
    // Initialize last activity time
    this.lastActivityTime = Date.now();
    this.storage.setItem(
      this.LAST_ACTIVITY_KEY,
      this.lastActivityTime.toString()
    );

    // Listen for page visibility changes (handles tab switching, minimize, sleep/wake)
    this.visibilityChangeListener = () => {
      if (document.hidden) {
        // Page is hidden (tab switched, minimized, or system going to sleep)
        console.log("📴 Page hidden - saving last activity time");
      } else {
        // Page is visible again (tab switched back, window restored, or system woke up)
        console.log("📱 Page visible - checking session timeout");
        this.checkSessionTimeout();
      }
    };
    document.addEventListener(
      "visibilitychange",
      this.visibilityChangeListener
    );

    // Listen for beforeunload (handles browser close, tab close, navigation away)
    this.beforeUnloadListener = () => {
      console.log(
        "🔄 Page unloading (beforeunload) - attempting logout API call"
      );
      this.callLogoutAPIOnUnload();
    };
    window.addEventListener("beforeunload", this.beforeUnloadListener);

    // Also listen for pagehide (more reliable on mobile and some browsers)
    // This fires when the page is being unloaded, including browser/tab close
    window.addEventListener("pagehide", (event) => {
      console.log("🔄 Page hiding (pagehide) - attempting logout API call");
      // Check if page is being unloaded (not just hidden)
      if (event.persisted) {
        // Page is being cached (e.g., back/forward navigation)
        console.log("📦 Page is being cached, not calling logout");
      } else {
        // Page is being unloaded (browser/tab close)
        this.callLogoutAPIOnUnload();
      }
    });
  }

  ngOnDestroy(): void {
    // Clean up event listeners
    if (this.visibilityChangeListener) {
      document.removeEventListener(
        "visibilitychange",
        this.visibilityChangeListener
      );
    }
    if (this.beforeUnloadListener) {
      window.removeEventListener("beforeunload", this.beforeUnloadListener);
    }
    if (this.timeoutHandle) {
      window.clearTimeout(this.timeoutHandle);
    }
    if (this.checkIntervalHandle) {
      window.clearInterval(this.checkIntervalHandle);
    }
  }

  public startSessionTimeout(): void {
    // Clear any existing timeout
    if (this.timeoutHandle) {
      window.clearTimeout(this.timeoutHandle);
    }

    // Update last activity time
    this.updateLastActivityTime();

    // Set timeout for the session
    this.timeoutHandle = window.setTimeout(() => {
      this.performLogout("Session timeout: User inactive for 10 minutes");
    }, this.sessionTimeout);

    // Also set up an interval to check timestamp-based timeout
    // This handles cases where system was asleep and setTimeout didn't fire
    if (this.checkIntervalHandle) {
      window.clearInterval(this.checkIntervalHandle);
    }
    this.checkIntervalHandle = window.setInterval(() => {
      this.checkSessionTimeout();
    }, 60000); // Check every minute
  }

  public resetSessionTimeout(): void {
    // Update last activity timestamp
    this.updateLastActivityTime();

    // Restart the timeout
    window.clearTimeout(this.timeoutHandle);
    this.startSessionTimeout();
  }

  /**
   * Mark the current time as login time to prevent logout API calls
   * during the grace period after login (prevents false logout calls during navigation)
   */
  public markLoginTime(): void {
    const loginTime = Date.now();
    sessionStorage.setItem(this.LOGIN_TIMESTAMP_KEY, loginTime.toString());
    console.log(
      "✅ Login timestamp marked:",
      new Date(loginTime).toISOString()
    );
  }

  /**
   * Check if we're within the grace period after login
   * Returns true if we should skip logout API calls
   */
  private isWithinLoginGracePeriod(): boolean {
    const loginTimestamp = sessionStorage.getItem(this.LOGIN_TIMESTAMP_KEY);
    if (!loginTimestamp) {
      return false; // No login timestamp, not in grace period
    }

    const loginTime = parseInt(loginTimestamp, 10);
    const now = Date.now();
    const timeSinceLogin = now - loginTime;

    if (timeSinceLogin < this.LOGIN_GRACE_PERIOD) {
      console.log(
        `⏱️ Within login grace period (${Math.round(timeSinceLogin)}ms < ${
          this.LOGIN_GRACE_PERIOD
        }ms), skipping logout API call`
      );
      return true;
    }

    // Grace period expired, clear the timestamp
    sessionStorage.removeItem(this.LOGIN_TIMESTAMP_KEY);
    return false;
  }

  private updateLastActivityTime(): void {
    this.lastActivityTime = Date.now();
    this.storage.setItem(
      this.LAST_ACTIVITY_KEY,
      this.lastActivityTime.toString()
    );
  }

  private checkSessionTimeout(): void {
    // Get stored last activity time
    const storedTime = this.storage.getItem(this.LAST_ACTIVITY_KEY);
    if (!storedTime) {
      // No stored time, update it now
      this.updateLastActivityTime();
      return;
    }

    const lastActivity = parseInt(storedTime, 10);
    const now = Date.now();
    const timeSinceLastActivity = now - lastActivity;

    // If more than 10 minutes have passed, logout
    if (timeSinceLastActivity >= this.sessionTimeout) {
      console.log(
        `⏰ Timestamp check: ${Math.round(
          timeSinceLastActivity / 1000 / 60
        )} minutes since last activity - logging out`
      );
      this.performLogout(
        "Session timeout detected via timestamp check (system may have been asleep)"
      );
    } else {
      // Update the stored time if it's valid
      this.lastActivityTime = lastActivity;
    }
  }

  private performLogout(reason: string): void {
    console.log(`⏰ ${reason}, initiating auto-logout...`);

    // Clear intervals and timeouts
    if (this.timeoutHandle) {
      window.clearTimeout(this.timeoutHandle);
    }
    if (this.checkIntervalHandle) {
      window.clearInterval(this.checkIntervalHandle);
    }

    // Call logout API to notify backend (fire-and-forget)
    this.callLogoutAPI();

    // Clear filter selections before auto-logout
    this.clearAllFilterSelections();

    // Clear ALL localStorage and sessionStorage completely
    console.log('🧹 Auto-logout: Clearing all localStorage and sessionStorage...');
    
    try {
      localStorage.clear();
      sessionStorage.clear();
      console.log('✅ All storage cleared on auto-logout');
    } catch (error) {
      console.error('❌ Error clearing storage on auto-logout:', error);
      // Fallback to individual removal
      this.storage.removeItem("user-info");
      this.storage.removeItem("sites-info");
      this.storage.removeItem("module-info");
      this.storage.removeItem(this.LAST_ACTIVITY_KEY);
      sessionStorage.removeItem("current-site");
      sessionStorage.removeItem("visitedUserManagementSites");
      sessionStorage.removeItem("admin_id");
      sessionStorage.removeItem(this.LOGIN_TIMESTAMP_KEY);
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      localStorage.removeItem("completeModule");
      localStorage.removeItem("corporateModule");
      localStorage.removeItem("labModule");
      localStorage.removeItem("editModule");
      localStorage.removeItem("GROUP");
      this.clearNotificationStorage();
      localStorage.removeItem("notification_theme");
      localStorage.removeItem("uploadedpath");
    }

    this.selectedSite = null;
    this.router.navigate(["/login"]);
  }
  public autologoutTimeout(): void {
    this.timeoutHandle = window.setTimeout(() => {
      // Clear filter selections before auto-logout
      this.clearAllFilterSelections();

      // Clear ALL localStorage and sessionStorage completely
      console.log('🧹 Auto-logout timeout: Clearing all storage...');
      try {
        localStorage.clear();
        sessionStorage.clear();
        console.log('✅ All storage cleared on auto-logout timeout');
      } catch (error) {
        console.error('❌ Error clearing storage:', error);
        // Fallback
        this.storage.removeItem("user-info");
        this.storage.removeItem("sites-info");
        this.storage.removeItem("module-info");
        sessionStorage.removeItem("current-site");
        localStorage.removeItem("completeModule");
        localStorage.removeItem("corporateModule");
        localStorage.removeItem("labModule");
        localStorage.removeItem("editModule");
        localStorage.removeItem("GROUP");
      }
      
      this.selectedSite = null;
      this.router.navigate(["/login"]);
    }, this.logoutTimeout);
  }
  private callLogoutAPI(): void {
    // Skip logout API call if we're within the grace period after login
    // This prevents false logout calls during navigation after login
    if (this.isWithinLoginGracePeriod()) {
      console.log("⏱️ Skipping logout API call - within login grace period");
      return;
    }

    // Get token before clearing storage
    const token = localStorage.getItem("token");

    // Call logout API to notify backend (fire-and-forget)
    if (token) {
      const logoutUrl = `${this.nodeURL}auth/logout`;
      console.log("🚀 Auto-logout: Calling logout API:", logoutUrl);

      fetch(logoutUrl, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        keepalive: true, // Ensures request completes even if page navigates away
      })
        .then((response) => {
          console.log(
            "✅ Auto-logout: Logout API called successfully, status:",
            response.status
          );
        })
        .catch((err) => {
          console.log("⚠️ Auto-logout: Logout API call failed:", err);
        });
    } else {
      console.warn("⚠️ Auto-logout: No token found, skipping logout API call");
    }
  }

  private callLogoutAPIOnUnload(): void {
    // Skip logout API call if we're within the grace period after login
    // This prevents false logout calls during navigation after login
    if (this.isWithinLoginGracePeriod()) {
      console.log(
        "⏱️ Skipping logout API call on unload - within login grace period"
      );
      return;
    }

    // Get token before clearing storage
    const token = localStorage.getItem("token");

    if (token) {
      const logoutUrl = `${this.nodeURL}auth/logout`;

      // Use fetch with keepalive for reliable delivery during page unload
      // This ensures the request completes even during system shutdown/browser close
      fetch(logoutUrl, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        keepalive: true, // Critical: ensures request completes during unload
      })
        .then((response) => {
          console.log(
            "✅ Unload: Logout API called successfully, status:",
            response.status
          );
        })
        .catch((err) => {
          console.log("⚠️ Unload: Logout API call failed:", err);
        });
    }
  }

  private clearAllFilterSelections(): void {
    try {
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith("reportFilter_")) {
          keysToRemove.push(key);
        }
      }

      keysToRemove.forEach((key) => {
        localStorage.removeItem(key);
        console.log(
          `Auto-logout cleanup: Cleared filter selection for key: ${key}`
        );
      });

      console.log(
        `Auto-logout cleanup: Cleared ${keysToRemove.length} filter selections`
      );
    } catch (e) {
      console.error("Error clearing filter selections during auto-logout:", e);
    }
  }

  private clearNotificationStorage(): void {
    try {
      // Clear all app_notifications_* keys from localStorage
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith("app_notifications_")) {
          keysToRemove.push(key);
        }
      }

      keysToRemove.forEach((key) => {
        localStorage.removeItem(key);
        console.log(`Auto-logout cleanup: Cleared notification key: ${key}`);
      });

      if (keysToRemove.length > 0) {
        console.log(
          `Auto-logout cleanup: Cleared ${keysToRemove.length} notification keys`
        );
      }
    } catch (e) {
      console.error(
        "Error clearing notification storage during auto-logout:",
        e
      );
    }
  }
}
